import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView,
  TouchableWithoutFeedback,
  Keyboard,
  Platform,
  Image,
  TouchableOpacity,
} from 'react-native';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../apiConfig.js';

export default function CreateAccount() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    confirmPassword: '',
    email: '',
    businessName: '',
    businessLogo: null, // will store the file path string from backend
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null); // Store the actual file
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null); // Store preview URL
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  // small helper to set fields like onChangeText('firstName', text)
  const setField = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  // Web image picker using a hidden input[type=file]
  const openFilePicker = () => fileInputRef.current?.click();
  
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a JPG or PNG image file.');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Logo file size must be less than 5MB.');
      return;
    }
    
    // Store the file and create preview
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Upload the logo immediately
    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_BASE_URL}/api/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        // Store the file path returned from backend
        setFormData((p) => ({ ...p, businessLogo: response.data.file_path }));
        alert('Logo uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo. Please try again.');
      setSelectedFile(null);
      setLogoPreviewUrl(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCreateAccount = async () => {
    setErrorMessage('');
    setPasswordError('');

    // Validate required fields
    if (!formData.firstName.trim()) {
      alert('Please enter your first name.');
      return;
    }

    if (!formData.lastName.trim()) {
      alert('Please enter your last name.');
      return;
    }

    if (!formData.email.trim()) {
      alert('Please enter your email address.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address.');
      return;
    }

    if (!formData.phone.trim()) {
      alert('Please enter your phone number.');
      return;
    }

    // Validate password
    if (!formData.password || formData.password.length < 6) {
      alert('Password must be at least 6 characters long.');
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
    if (!specialCharRegex.test(formData.password)) {
      alert('Password must contain at least one special character.');
      setPasswordError('Password must contain at least one special character');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match. Please try again.');
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      setCreatingAccount(true);
      const confirmationToken = Math.random().toString(36).slice(2, 12);
      const confirmationExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

      const payload = {
        phone: formData.phone,
        password: formData.password,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        businessName: formData.businessName,
        businessLogo: formData.businessLogo,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        user_confirmed: false,
        confirmation_token: confirmationToken,
        confirmation_expires_at: confirmationExpiresAt,
      };

      const response = await axios.post(`${API_BASE_URL}/api/create-account`, payload);

      const code = response?.data?.code;

      if (code === 'EXPIRED_TOKEN') {
        alert('Your confirmation token has expired. Please request a new one.');
        const resendResponse = await axios.post(`${API_BASE_URL}/api/resend-token`, { email: formData.email });
        if (resendResponse.status === 200) {
          alert('A new confirmation token has been sent to your email.');
        } else {
          alert('Failed to resend confirmation token. Please try again later.');
        }
      } else if (code === 'PENDING_CONFIRMATION') {
        alert('Your email confirmation is still pending. Please check your email.');
        navigate('/login', { replace: true });
      } else if (code === 'ACCOUNT_CREATED') {
        alert('Account created successfully. Please check your email to confirm your account. Check your SPAM folder if you do not see it.');
        navigate('/login', { replace: true });
      } else if (code === 'ALREADY_REGISTERED') {
        alert('You are already a registered user. Please log in.');
        navigate('/login', { replace: true });
      } else if (code === 'NEW_TOKEN_SENT') {
        alert('A new confirmation email has been sent. Please confirm your email. Check your SPAM folder if you do not see it.');
        navigate('/login', { replace: true });
      } else {
        alert('Failed to create account on the server.');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      setErrorMessage('Failed to create account. Please try again later.');
      alert('Failed to create account. Please try again later.');
    } finally {
      setCreatingAccount(false);
    }
  };

  return (
    <div style={styles.pageContainer}>
      <div style={styles.backgroundOverlay}>
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          {/* Hidden file input for web image selection */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onFilePicked}
          />

          <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
            <ScrollView
              contentContainerStyle={{
                flexGrow: 1,
                justifyContent: 'center',
                alignItems: 'center',
                padding: 20,
                minHeight: '100vh',
              }}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              <View style={styles.container}>
                <View style={styles.logoContainer}>
                  <Text style={styles.logoText}>TradeBook</Text>
                  <Text style={styles.logoSubtext}>Business Management</Text>
                </View>

                <Text style={styles.header}>Create Your Account</Text>
                <Text style={styles.subheader}>Join TradeBook to manage your business</Text>

                {!!errorMessage && (
                  <View style={styles.errorContainer}>
                    <Text style={styles.error}>{errorMessage}</Text>
                  </View>
                )}

                <View style={styles.formSection}>
                  <Text style={styles.sectionTitle}>Personal Information</Text>

                  <View style={styles.inputRow}>
                    <View style={styles.inputHalf}>
                      <Text style={styles.label}>First Name</Text>
                      <input
                        style={styles.input}
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={(e) => setField('firstName', e.target.value)}
                      />
                    </View>
                    <View style={styles.inputHalf}>
                      <Text style={styles.label}>Last Name</Text>
                      <input
                        style={styles.input}
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={(e) => setField('lastName', e.target.value)}
                      />
                    </View>
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Phone Number</Text>
                    <input
                      style={styles.input}
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                    />
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Email Address</Text>
                    <input
                      style={styles.input}
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setField('email', e.target.value)}
                    />
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Password</Text>
                    <input
                      style={styles.input}
                      type="password"
                      placeholder="Create a secure password"
                      value={formData.password}
                      onChange={(e) => setField('password', e.target.value)}
                    />
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Confirm Password</Text>
                    <input
                      style={styles.input}
                      type="password"
                      placeholder="Re-enter your password"
                      value={formData.confirmPassword}
                      onChange={(e) => setField('confirmPassword', e.target.value)}
                    />
                  </View>

                  {!!passwordError && (
                    <View style={styles.errorContainer}>
                      <Text style={styles.error}>{passwordError}</Text>
                    </View>
                  )}
                </View>

                <View style={styles.formSection}>
                  <Text style={styles.sectionTitle}>Business Information</Text>
                  <Text style={styles.sectionSubtitle}>Optional - for invoicing and quotes</Text>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Business Name</Text>
                    <input
                      style={styles.input}
                      placeholder="Your Business Name"
                      value={formData.businessName}
                      onChange={(e) => setField('businessName', e.target.value)}
                    />
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Business Logo</Text>
                    <TouchableOpacity style={styles.logoButton} onPress={openFilePicker}>
                      <Text style={styles.logoButtonText}>
                        {formData.businessLogo ? 'Change Logo' : 'Select Logo'}
                      </Text>
                    </TouchableOpacity>
                    {!!logoPreviewUrl && (
                      <View style={styles.logoPreviewContainer}>
                        <Image source={{ uri: logoPreviewUrl }} style={styles.logoPreview} />
                        <Text style={styles.logoPreviewText}>Logo selected</Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Business Address</Text>
                    <input
                      style={styles.input}
                      placeholder="123 Business Street"
                      value={formData.address}
                      onChange={(e) => setField('address', e.target.value)}
                    />
                  </View>

                  <View style={styles.inputRow}>
                    <View style={styles.inputThird}>
                      <Text style={styles.label}>City</Text>
                      <input
                        style={styles.input}
                        placeholder="City"
                        value={formData.city}
                        onChange={(e) => setField('city', e.target.value)}
                      />
                    </View>
                    <View style={styles.inputThird}>
                      <Text style={styles.label}>State</Text>
                      <input
                        style={styles.input}
                        placeholder="State"
                        value={formData.state}
                        onChange={(e) => setField('state', e.target.value)}
                      />
                    </View>
                    <View style={styles.inputThird}>
                      <Text style={styles.label}>Zip Code</Text>
                      <input
                        style={styles.input}
                        placeholder="12345"
                        type="number"
                        value={formData.zip}
                        onChange={(e) => setField('zip', e.target.value)}
                      />
                    </View>
                  </View>
                </View>

                <TouchableOpacity 
                  style={[styles.createAccountButton, creatingAccount && styles.disabledButton]} 
                  onPress={handleCreateAccount}
                  disabled={creatingAccount}
                >
                  <Text style={styles.createAccountButtonText}>
                    {creatingAccount ? 'Creating Account...' : 'Create Account'}
                  </Text>
                </TouchableOpacity>

                <View style={styles.divider}>
                  <View style={styles.dividerLine} />
                  <Text style={styles.dividerText}>or</Text>
                  <View style={styles.dividerLine} />
                </View>

                <TouchableOpacity style={styles.cancelButton} onPress={() => navigate('/login')}>
                  <Text style={styles.cancelButtonText}>Back to Sign In</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </TouchableWithoutFeedback>
        </KeyboardAvoidingView>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  backgroundOverlay: {
    flex: 1,
    backgroundColor: '#fff',
  },
  container: {
    width: '100%',
    maxWidth: 800, // Made much wider - was 400px
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 40, // Increased padding for better proportions
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  logoText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  logoSubtext: {
    fontSize: 16,
    color: '#6B7280',
  },
  header: {
    fontSize: 32, // Increased font size
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
    color: '#333',
  },
  subheader: {
    fontSize: 18, // Increased font size
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 30, // Increased spacing
  },
  errorContainer: {
    backgroundColor: '#FEE2E2',
    padding: 10,
    borderRadius: 5,
    marginBottom: 15,
  },
  error: {
    color: '#B91C1C',
    textAlign: 'center',
  },
  formSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 20, // Increased font size
    fontWeight: 'bold',
    marginBottom: 15, // Increased spacing
    color: '#333',
  },
  sectionSubtitle: {
    fontSize: 16, // Increased font size
    color: '#6B7280',
    marginBottom: 15, // Increased spacing
  },
  inputRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  inputHalf: {
    width: '48%',
  },
  inputThird: {
    width: '30%',
  },
  fieldContainer: {
    marginBottom: 20, // Increased spacing
  },
  label: {
    marginBottom: 8, // Increased spacing
    fontWeight: 'bold',
    color: '#333',
    fontSize: 16, // Added font size
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    padding: 15, // Increased padding
    borderRadius: 8, // Larger border radius
    color: '#333',
    backgroundColor: '#F9FAFB',
    fontSize: 16, // Added font size
  },
  logoButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    alignItems: 'center',
    marginBottom: 10,
  },
  logoButtonText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  logoPreviewContainer: {
    alignItems: 'center',
    marginTop: 10,
  },
  logoPreview: {
    width: 100,
    height: 100,
    borderRadius: 10,
  },
  logoPreviewText: {
    marginTop: 5,
    color: '#6B7280',
  },
  createAccountButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 15,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 20,
  },
  createAccountButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#D1D5DB',
  },
  dividerText: {
    marginHorizontal: 10,
    color: '#6B7280',
  },
  cancelButton: {
    backgroundColor: '#FF3B30',
    paddingVertical: 15,
    borderRadius: 5,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  disabledButton: {
    backgroundColor: '#9CA3AF',
    opacity: 0.7,
  },
});
